'use client';

import getTags from '@/actions/tags/getTags';
import getTagsCategory from '@/actions/tags/getTagsCategory';
import { cn } from '@/lib/utils';
import { TagCategory, Tag } from '@/types/tags';
import { a, useSpring } from '@react-spring/web';
import { useState, useEffect, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { MinusIcon, PlusIcon, XIcon } from 'lucide-react';
import { Locale } from '@/enums/locale.enum';

interface ComponentProps {
  lang: Locale;
  title: string;
}

export default function TagSelector(props: ComponentProps) {
  const { title, lang } = props;

  const [tagsCategory, setTagsCategory] = useState<TagCategory[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const createQueryString = useCallback(
    (name: string, value?: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );

  const [selectedCategory, setSelectedCategory] = useState<TagCategory>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  function toggleTag(tag: string) {
    if (selectedTags.includes(tag))
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    else setSelectedTags([...selectedTags, tag]);
  }

  function clearTags() {
    setSelectedTags([]);
  }

  function selectAllTags(tab: string) {
    // @ts-ignore
    setSelectedTags(tags[tab]);
  }

  const buttonStyle = useSpring({
    opacity: !!selectedTags?.length ? 1 : 0,
    config: { tension: 280, friction: 60, duration: 200 },
  });

  useEffect(() => {
    const tagsParams = encodeURIComponent(selectedTags.join(';'));

    let redirectUrl = pathname;
    if (tagsParams) redirectUrl += '?' + createQueryString('tags', tagsParams);

    router.push(redirectUrl);
  }, [selectedTags]);

  useEffect(() => {
    getTagsCategory({
      locale: lang,
      baseUrl: true,
    }).then((res) => {
      if (!!res.data) {
        const sorted = res.data.data
          .sort((a, b) => a.indexId - b.indexId)
          .filter((el) => el.name.toLowerCase() !== 'blog tags');
        setTagsCategory(sorted);
        if (!selectedCategory && sorted.length > 0) {
          setSelectedCategory(sorted[0]);
        }
      }
    });

    getTags({
      locale: lang,
      baseUrl: true,
    }).then((res) => {
      if (!!res.data) {
        setTags(res.data.data);
      }
    });
  }, [lang]);

  return (
    <div className='mb-20 text-[#101011]'>
      <div className='sm:mb-[28px] mb-19'>
        {tagsCategory.map((category, index) => (
          <div
            className={cn(
              'sm:text-[10px] inline-flex text-[16px] items-center justify-center whitespace-nowrap rounded-md  py-1 ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
              {
                'font-bold': selectedCategory?.id === category.id,
                'font-medium': selectedCategory?.id !== category.id,
                'sm:mr-[14px] mr-[48px]': index !== tagsCategory.length - 1,
              }
            )}
            onClick={() => setSelectedCategory(category)}
            key={category.id}
          >
            {category.name}
            {selectedCategory?.name === category.name ? (
              <span className='sm:pl-[4px] pl-[8px]'>
                <img
                  src='/svg/projects/icons_chevron.svg'
                  className='sm:w-2  sm:h-2 w-4 h-4'
                />
              </span>
            ) : (
              <span className='sm:pl-[4px] pl-[8px]'>
                <img
                  src='/svg/projects/rivet-icons_chevron-up.svg'
                  className='sm:w-2  sm:h-2 w-4 h-4 '
                />
              </span>
            )}
          </div>
        ))}
      </div>
      {!!selectedCategory?.tags?.length && (
        <div>
          <div className='flex text-black justify-between mt-[19px] sm:mt-0 sm:mb-6 mb-4'>
            <div
              className='uppercase text-[14px] leading-[120%] tracking-normal font-bold rounded-full cursor-pointer'
              onClick={() =>
                setSelectedTags(selectedCategory.tags.map((i) => i.name))
              }
            >
              {title}
            </div>
            <a.div
              style={buttonStyle}
              className={cn(
                'uppercase text-[14px] leading-[120%] tracking-normal font-bold rounded-full cursor-pointer flex items-center gap-1'
              )}
              onClick={clearTags}
            >
              <span>Clear</span>
              <XIcon className='ml-1 h-4 w-4' />
            </a.div>
          </div>

          <ul className='flex gap-2 flex-wrap'>
            {selectedCategory.tags.map((tag) => (
              <li key={tag.id}>
                <Badge
                  variant={
                    selectedTags.includes(tag.name) ? 'default' : 'outline'
                  }
                  className={cn(
                    'rounded-full flex items-center gap-2 transition hover:bg-primary/30 duration-300 py-1 md:py-2 px-4 cursor-pointer text-[20px] leading-[120%] tracking-[-0.03em] sm:text-[14.5px] font-normal',
                    {
                      'hover:#0D1524': selectedTags.includes(tag.name),
                    }
                  )}
                  onClick={() => toggleTag(tag.name)}
                >
                  <span>{tag.name}</span>
                  {selectedTags.includes(tag.name) ? (
                    <MinusIcon className='w-4 h-4 sm:w-3 sm:h-3' />
                  ) : (
                    <PlusIcon className='w-4 h-4 sm:w-3 sm:h-3' />
                  )}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
